/* ==========================================================================
                              INFINITE SCROLL — v3
   ========================================================================== */

class InfiniteScroll extends HTMLElement {
  constructor () {
    super();

    this.button         = this.querySelector('button');
    this.loadingOverlay = this.querySelector('.loading-overlay');
    this.nextUrl        = this.dataset.nextUrl || null;
    this.previousUrl    = this.dataset.previousUrl || null;
    this.triggerType    = this.dataset.triggerType;             // "button" | "infinite"
    this.isLoading      = false;
    this.currentPage    = parseInt(this.dataset.currentPage) || 1;
    this.pages          = new Map();
    this.resolveNextPagePromise = null;
    this.resolvePreviousPagePromise = null;
    this.hasInitialized = false;
    this.isReplacing    = false;

    // Initialize previous URL based on current page
    this.updatePreviousUrl();

    /* — button «load more» — */
    if (this.button) {
      this.button.addEventListener('click', () => this.fetchNextPage());
    }

    if (this.triggerType === 'infinite') { this.initObserver(); }

    /* — scroll position restoration — */
    this.initScrollRestoration();

    /* — handle initial page load — */
    console.log(`[Init] Step 2: Handle initial load at ${Date.now()}`);
    this.handleInitialLoad();

    /* — prefetch initial pages — */
    console.log(`[Init] Step 3: Prefetching pages at ${Date.now()}`);
    this.prefetchPage('next');
    this.prefetchPage('previous');

    /* — filter updates — */
    this.boundHandleFilterUpdate = this.handleFilterUpdate.bind(this);
    document.addEventListener('filter:update', this.boundHandleFilterUpdate);

    /* — browser navigation — */
    this.boundHandlePopState = this.handlePopState.bind(this);
    window.addEventListener('popstate', this.boundHandlePopState);

    /* — product card click tracking — */
    this.initProductCardTracking();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }

    document.removeEventListener('filter:update', this.boundHandleFilterUpdate);
    window.removeEventListener('popstate', this.boundHandlePopState);
    if (this.productCardClickHandler) {
      document.removeEventListener('click', this.productCardClickHandler);
    }
  }

  /* ---------- IntersectionObserver ---------- */
  initObserver () {
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target.dataset.direction === 'previous') {
            this.fetchPreviousPage();
          } else {
            this.fetchNextPage();
          }
        }
      });
    }, { rootMargin: '500px' });

    // Observe next sentinel (this element)
    this.observer.observe(this);

    // Observe previous sentinel if it exists
    const previousSentinel = document.querySelector('[data-direction="previous"]');
    if (previousSentinel) {
      this.observer.observe(previousSentinel);
    }
  }

  /* ---------- Load new page ---------- */
  async fetchNextPage () {
    if (this.isLoading || !this.nextUrl) { return; }

    const nextPageNumber = this.currentPage + 1;
    let pageContent = this.pages.get(nextPageNumber);

    if (!pageContent) {
      const promise = new Promise((resolve) => {
        this.resolveNextPagePromise = resolve;
      });

      this.prefetchPage('next');
      await promise;
      pageContent = this.pages.get(nextPageNumber);
      if (!pageContent) return;
    }

    /* --- preserve scroll position before loading --- */
    const scrollTop = window.scrollY;

    this.isLoading = true;
    this.toggleLoadingState(true);

    /* --- restore scroll position after loading state change --- */
    window.scrollTo({
      top: scrollTop,
      behavior: 'instant'
    });

    try {
      const nextPageItemElements = this.getProductsForPage(nextPageNumber);
      if (!nextPageItemElements) return;

      /* --- filter duplicates --- */
      const uniqueItems = this.filterDuplicates(nextPageItemElements);
      if (uniqueItems.length === 0) {
        console.warn(`[Infinite Scroll] All products from page ${nextPageNumber} already exist`);
        this.isLoading = false;
        this.toggleLoadingState(false);
        return;
      }

      const html = new DOMParser().parseFromString(pageContent, 'text/html');
      const newPagination = html.getElementById('pagination-ajax');

      /* --- update DOM --- */
      document.getElementById('product-grid-ajax').append(...uniqueItems);

      /* --- update URLs --- */
      const newInfiniteScroll = newPagination?.querySelector('infinite-scroll');
      if (newInfiniteScroll) {
        const nextUrl = newInfiniteScroll.dataset.nextUrl;
        this.nextUrl = nextUrl || null;

        // Update the data attributes on this element
        if (nextUrl) {
          this.dataset.nextUrl = nextUrl;
        } else {
          delete this.dataset.nextUrl;
        }
      } else {
        this.nextUrl = null;
      }

      this.currentPage++;
      this.updatePreviousUrl();

      /* --- update URL --- */
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', this.currentPage.toString());
      newUrl.hash = '';
      history.pushState('', '', newUrl.toString());

      /* --- if end — disconect observer --- */
      if (!this.nextUrl && this.observer) { this.observer.disconnect(); }

      /* --- prefetch next page --- */
      requestIdleCallback(() => {
        this.prefetchPage('next');
      });

    } catch (err) {
      console.error('Infinite Scroll error:', err);
    } finally {
      this.toggleLoadingState(false);
      this.isLoading = false;

      if (!this.nextUrl && this.button) { this.button.remove(); }
    }
  }

  /* ---------- Load previous page ---------- */
  async fetchPreviousPage () {
    if (this.isLoading || !this.previousUrl || this.currentPage <= 1) { return; }

    const previousPageNumber = this.currentPage - 1;
    let pageContent = this.pages.get(previousPageNumber);

    if (!pageContent) {
      const promise = new Promise((resolve) => {
        this.resolvePreviousPagePromise = resolve;
      });

      this.prefetchPage('previous');
      await promise;
      pageContent = this.pages.get(previousPageNumber);
      if (!pageContent) return;
    }

    this.isLoading = true;

    try {
      const previousPageItemElements = this.getProductsForPage(previousPageNumber);
      if (!previousPageItemElements) return;

      /* --- filter duplicates --- */
      const uniqueItems = this.filterDuplicates(previousPageItemElements);
      if (uniqueItems.length === 0) {
        console.warn(`[Infinite Scroll] All products from page ${previousPageNumber} already exist`);
        this.isLoading = false;
        return;
      }

      /* --- preserve scroll position --- */
      const scrollTop = window.scrollY;
      const grid = document.getElementById('product-grid-ajax');
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      /* --- prepend new content --- */
      grid.prepend(...uniqueItems);

      /* --- adjust scroll position --- */
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      this.currentPage--;
      this.updatePreviousUrl();

      /* --- update URL --- */
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', this.currentPage.toString());
      newUrl.hash = '';
      history.pushState('', '', newUrl.toString());

      /* --- prefetch previous page --- */
      requestIdleCallback(() => {
        this.prefetchPage('previous');
      });

    } catch (err) {
      console.error('Previous page fetch error:', err);
    } finally {
      this.isLoading = false;
    }
  }

  /* ---------- Update previous URL ---------- */
  updatePreviousUrl () {
    if (this.currentPage > 1) {
      const url = new URL(window.location.href);
      url.searchParams.set('page', (this.currentPage - 1).toString());
      this.previousUrl = url.toString();
    } else {
      this.previousUrl = null;
    }
  }

  /* ---------- UI ---------- */
  toggleLoadingState (state) {
    if (!this.button) return;
    this.button.classList.toggle('loading', state);
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.toggle('hidden', !state);
    }
  }

  /* ---------- Prefetch Pages ---------- */
  async prefetchPage (direction) {
    const pageNumber = direction === 'next' ? this.currentPage + 1 : this.currentPage - 1;

    if (pageNumber < 1 || this.pages.has(pageNumber)) {
      this.resolvePromise(direction);
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('page', pageNumber.toString());
    url.hash = '';

    try {
      const htmlText = await fetch(url.toString()).then(r => r.text());
      this.pages.set(pageNumber, htmlText);
    } catch (err) {
      console.error('Prefetch error:', err);
    } finally {
      this.resolvePromise(direction);
    }
  }

  resolvePromise (direction) {
    if (direction === 'next' && this.resolveNextPagePromise) {
      this.resolveNextPagePromise();
      this.resolveNextPagePromise = null;
    } else if (direction === 'previous' && this.resolvePreviousPagePromise) {
      this.resolvePreviousPagePromise();
      this.resolvePreviousPagePromise = null;
    }
  }

  getProductsForPage (page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.getElementById('product-grid-ajax');
    if (!gridElement) return;

    return Array.from(gridElement.children);
  }

  /* ---------- Filter Duplicate Products (Lightweight) ---------- */
  filterDuplicates (items) {
    // Build set of existing product IDs in current DOM (read-only, no manipulation)
    const existingIds = new Set(
      Array.from(document.querySelectorAll('#product-grid-ajax [id^="product-"]'))
        .map(el => el.id)
    );

    // Filter out items that already exist
    return items.filter(item => {
      if (!item.id || !item.id.startsWith('product-')) return true;
      return !existingIds.has(item.id);
    });
  }

  /* ---------- Initial Load Handling ---------- */
  async handleInitialLoad () {
    // Prevent multiple initial loads
    if (this.hasInitialized) {
      console.log(`[Infinite Scroll] Already initialized, skipping`);
      return;
    }
    this.hasInitialized = true;

    const url = new URL(window.location.href);
    const urlPage = parseInt(url.searchParams.get('page')) || 1;
    const storedState = this.getStoredCollectionState();
    let targetPage = urlPage;
    let shouldRestore = false;

    // Check if we're coming back from a product page (multiple signals)
    const isBackNavigation = document.referrer.includes('/products/') ||
                            performance.navigation?.type === 2 || // Back/forward navigation
                            performance.getEntriesByType('navigation')[0]?.type === 'back_forward';

    console.log(`[Infinite Scroll] Initial load - URL page: ${urlPage}, stored state:`, storedState, `isBackNavigation: ${isBackNavigation}`);

    // Use stored state if it's valid and recent (regardless of back navigation detection)
    if (storedState && this.shouldUseStoredState(storedState)) {
      targetPage = storedState.page;
      shouldRestore = true;
      console.log(`[Infinite Scroll] Valid stored state found - restoring to page ${targetPage}, product: ${storedState.productId}`);
    }

    // If URL has a different page than the server-rendered page, replace content
    if (targetPage !== this.currentPage) {
      console.log(`[Infinite Scroll] Target page (${targetPage}) differs from server page (${this.currentPage}), replacing content`);
      await this.replaceWithTargetPage(targetPage);
    }

    // Handle scroll position restoration
    if (shouldRestore && storedState) {
      this.handleScrollRestoration(url, storedState, true);
      // Clear after using
      this.clearStoredCollectionState();
    } else if (url.hash) {
      // Handle hash-based scroll even without full restoration
      this.handleScrollRestoration(url, null, false);
    }
  }

  /* ---------- Handle Scroll Restoration ---------- */
  handleScrollRestoration(url, storedState, didPageChange) {
    // Prevent browser's default scroll restoration
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // Determine which product to scroll to
    let productId = null;

    // Priority 1: storedState from back navigation
    if (storedState && storedState.productId) {
      productId = storedState.productId;
      console.log(`[Infinite Scroll] Using stored product ID: ${productId}`);
    }
    // Priority 2: hash in URL
    else if (url.hash) {
      productId = url.hash.slice(1);
      console.log(`[Infinite Scroll] Using URL hash: ${productId}`);
    }

    // Scroll to the product if we have an ID
    if (productId) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        const element = document.getElementById(productId);
        if (element) {
          console.log(`[Infinite Scroll] Scrolling to product: ${productId}`);
          // Use instant behavior to avoid visible scrolling animation
          // Use 'nearest' to only scroll if element is out of view
          element.scrollIntoView({ behavior: 'instant', block: 'nearest' });
        } else {
          console.warn(`[Infinite Scroll] Product element not found: ${productId}`);
        }
      });
    }
  }

  /* ---------- Get Stored Collection State ---------- */
  getStoredCollectionState() {
    try {
      const stored = sessionStorage.getItem('collection_state');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.warn('[Infinite Scroll] Failed to retrieve collection state:', error);
      return null;
    }
  }

  /* ---------- Check if Stored State Should Be Used ---------- */
  shouldUseStoredState(storedState) {
    if (!storedState) return false;

    // Only use stored state if it's recent (within 5 minutes)
    const fiveMinutes = 5 * 60 * 1000;
    const isRecent = (Date.now() - storedState.timestamp) < fiveMinutes;

    // Check if we're on the same collection URL (without page parameter)
    const currentBaseUrl = window.location.pathname + window.location.search.replace(/[?&]page=\d+/, '');
    const storedBaseUrl = new URL(storedState.url).pathname + new URL(storedState.url).search.replace(/[?&]page=\d+/, '');

    return isRecent && currentBaseUrl === storedBaseUrl;
  }

  /* ---------- Replace Grid with Target Page ---------- */
  async replaceWithTargetPage(targetPage) {
    // Prevent multiple simultaneous replacements
    if (this.isReplacing) {
      console.log(`[Infinite Scroll] Already replacing content, skipping`);
      return;
    }

    this.isReplacing = true;

    try {
      const grid = document.getElementById('product-grid-ajax');
      if (!grid) return;

      console.log(`[Infinite Scroll] Fetching page ${targetPage} content`);

      // Fetch the target page content
      const url = new URL(window.location.href);
      url.searchParams.set('page', targetPage.toString());

      const response = await fetch(url.toString());
      const htmlText = await response.text();
      const parsedPage = new DOMParser().parseFromString(htmlText, 'text/html');
      const newGrid = parsedPage.getElementById('product-grid-ajax');
      const newPagination = parsedPage.getElementById('pagination-ajax');

      if (newGrid) {
        // Replace the entire grid content
        grid.innerHTML = newGrid.innerHTML;

        // Update current page state
        this.currentPage = targetPage;
        this.updatePreviousUrl();

        // Update next URL from the new pagination
        const newInfiniteScroll = newPagination?.querySelector('infinite-scroll');
        if (newInfiniteScroll) {
          this.nextUrl = newInfiniteScroll.dataset.nextUrl || null;
          console.log(`[Infinite Scroll] Updated nextUrl: ${this.nextUrl}`);
        } else {
          this.nextUrl = null;
          console.log(`[Infinite Scroll] No more pages`);
        }

        // Clear old page cache to prevent stale data
        this.pages.clear();

        // Cache only the current page
        this.pages.set(targetPage, htmlText);

        // Update browser URL to reflect current page
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('page', targetPage.toString());
        history.replaceState('', '', newUrl.toString());

        // Clear the stored state since we've used it
        this.clearStoredCollectionState();

        // Prefetch adjacent pages
        requestIdleCallback(() => {
          this.prefetchPage('next');
          this.prefetchPage('previous');
        });

        console.log(`[Infinite Scroll] Successfully replaced grid with page ${targetPage} content`);
      }
    } catch (error) {
      console.error('[Infinite Scroll] Error replacing grid content:', error);
    } finally {
      this.isReplacing = false;
    }
  }

  /* ---------- Clear Stored Collection State ---------- */
  clearStoredCollectionState() {
    try {
      sessionStorage.removeItem('collection_state');
      console.log('[Infinite Scroll] Cleared stored collection state');
    } catch (error) {
      console.warn('[Infinite Scroll] Failed to clear collection state:', error);
    }
  }

  /* ---------- Browser Navigation ---------- */
  async handlePopState () {
    // Prevent handling popstate during page replacement
    if (this.isReplacing) {
      console.log(`[Infinite Scroll] PopState ignored during page replacement`);
      return;
    }

    const url = new URL(window.location.href);
    const targetPage = parseInt(url.searchParams.get('page')) || 1;

    if (targetPage === this.currentPage) {
      // Just handle scroll restoration for same page
      this.handleScrollRestoration(url, null, false);
      return;
    }

    console.log(`[Infinite Scroll] PopState: navigating from page ${this.currentPage} to page ${targetPage}`);

    // Use replaceWithTargetPage for more reliable navigation
    await this.replaceWithTargetPage(targetPage);

    // Handle scroll restoration after page replacement
    this.handleScrollRestoration(url, null, true);
  }

  async loadToPage (targetPage) {
    if (targetPage === this.currentPage) return;

    const direction = targetPage > this.currentPage ? 'forward' : 'backward';
    const grid = document.getElementById('product-grid-ajax');

    if (direction === 'forward') {
      while (this.currentPage < targetPage) {
        await this.fetchNextPage();
      }
    } else {
      while (this.currentPage > targetPage) {
        await this.fetchPreviousPage();
      }
    }
  }

  /* ---------- Filter Updates ---------- */
  handleFilterUpdate () {
    this.pages.clear();

    if (this.resolveNextPagePromise) {
      this.resolveNextPagePromise();
      this.resolveNextPagePromise = null;
    }
    if (this.resolvePreviousPagePromise) {
      this.resolvePreviousPagePromise();
      this.resolvePreviousPagePromise = null;
    }

    this.prefetchPage('next');
    this.prefetchPage('previous');
  }

  /* ---------- Product Card Click Tracking ---------- */
  initProductCardTracking () {
    this.productCardClickHandler = (e) => {
      const productCard = e.target.closest('[id^="product-"]');
      if (productCard && productCard.id) {
        // Store collection state before navigating to product
        this.storeCollectionState(productCard.id);

        const currentUrl = new URL(window.location.href);
        currentUrl.hash = `#${productCard.id}`;
        history.replaceState('', '', currentUrl.toString());
      }
    };
    document.addEventListener('click', this.productCardClickHandler);
  }

  /* ---------- Store Collection State ---------- */
  storeCollectionState(productId) {
    try {
      const collectionState = {
        page: this.currentPage,
        productId: productId,
        url: window.location.href,
        timestamp: Date.now()
        // Note: We don't store scrollY because scroll positions are unreliable
        // when switching between different pages with different content layouts
      };

      sessionStorage.setItem('collection_state', JSON.stringify(collectionState));
      console.log(`[Infinite Scroll] Stored collection state: page ${this.currentPage}, product ${productId}`);
    } catch (error) {
      console.warn('[Infinite Scroll] Failed to store collection state:', error);
    }
  }

  /* ---------- Scroll Position Restoration ---------- */
  initScrollRestoration () {
    // Scroll restoration is now handled centrally in handleScrollRestoration()
    // This method is kept for compatibility but disabled

    // Disable browser's automatic scroll restoration to prevent conflicts
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }
}

customElements.define('infinite-scroll', InfiniteScroll);
